import React from "react";
import Icon from "components/AppIcon";
import Input from "components/ui/Input";

const JURISDICTIONS = [
  { value: "uk", label: "United Kingdom", regulator: "FCA", flag: "🇬🇧" },
  { value: "eu", label: "European Union", regulator: "ESMA / National NCAs", flag: "🇪🇺" },
  { value: "us", label: "United States", regulator: "SEC / FINRA", flag: "🇺🇸" },
  { value: "sg", label: "Singapore", regulator: "MAS", flag: "🇸🇬" },
  { value: "uae", label: "United Arab Emirates", regulator: "DFSA / ADGM", flag: "🇦🇪" },
  { value: "ch", label: "Switzerland", regulator: "FINMA", flag: "🇨🇭" },
  { value: "ca", label: "Canada", regulator: "OSC / IIROC", flag: "🇨🇦" },
  { value: "au", label: "Australia", regulator: "ASIC", flag: "🇦🇺" },
  { value: "other", label: "Other Jurisdiction", regulator: "Local regulator", flag: "🌍" },
];

const TAX_IMPLICATIONS = {
  uk: "Subject to UK corporation tax on UK-source income. SDLT may apply on property-backed assets. HMRC reporting obligations.",
  eu: "Subject to EU AIFMD requirements. Withholding tax treaties may apply. DAC6 reporting for certain arrangements.",
  us: "FATCA compliance required. FIRPTA may apply to real property interests. Qualified opportunity zone considerations.",
  sg: "Singapore tax resident entities benefit from extensive treaty network. GST registration may be required.",
  uae: "No corporate income tax in most free zones. VAT at 5% may apply. DIFC/ADGM entities have specific regimes.",
  ch: "Swiss withholding tax at 35% on dividends. Stamp duty on securities transfers. Cantonal tax variations.",
  ca: "Canadian withholding tax on dividends. Provincial tax variations. FAPI rules for foreign affiliates.",
  au: "Australian withholding tax on interest and dividends. GST may apply. FIRB approval for foreign investment.",
  other: "Please consult your local tax adviser for jurisdiction-specific implications.",
};

export default function StepJurisdiction({ data, onChange, errors }) {
  const handleJurisdictionSelect = (value) => {
    onChange({ ...data, jurisdiction: value });
  };

  const handleChange = (field) => (e) => {
    onChange({ ...data, [field]: e?.target?.value });
  };

  const selectedJurisdiction = JURISDICTIONS?.find((j) => j?.value === data?.jurisdiction);

  return (
    <div className="space-y-6">
      <p className="text-sm font-caption text-muted-foreground">
        Select your primary regulatory jurisdiction. This determines applicable compliance requirements, reporting obligations, and investment restrictions on the platform.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {JURISDICTIONS?.map((j) => {
          const isSelected = data?.jurisdiction === j?.value;
          return (
            <button
              key={j?.value}
              onClick={() => handleJurisdictionSelect(j?.value)}
              className={`text-left p-3 rounded-lg border-2 transition-all duration-200 ${
                isSelected
                  ? "border-primary bg-[rgba(59,76,76,0.07)]" :"border-border bg-card hover:border-secondary hover:shadow-sm"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-xl">{j?.flag}</span>
                <div className="flex-1 min-w-0">
                  <p className={`font-caption font-semibold text-sm ${isSelected ? "text-primary" : "text-foreground"}`}>
                    {j?.label}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{j?.regulator}</p>
                </div>
                {isSelected && <Icon name="Check" size={14} color="var(--color-primary)" />}
              </div>
            </button>
          );
        })}
      </div>
      {errors?.jurisdiction && <p className="text-error text-xs">{errors?.jurisdiction}</p>}
      {selectedJurisdiction && (
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
          <div className="flex items-start gap-2">
            <Icon name="AlertTriangle" size={16} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-caption font-semibold text-sm text-warning mb-1">
                Tax & Regulatory Implications — {selectedJurisdiction?.label}
              </p>
              <p className="text-xs text-muted-foreground">{TAX_IMPLICATIONS?.[selectedJurisdiction?.value]}</p>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-border">
        <Input
          label="Regulatory Reference Number"
          type="text"
          placeholder="e.g. FRN 123456"
          value={data?.regulatoryRef || ""}
          onChange={handleChange("regulatoryRef")}
          description="Your firm's registration number with the relevant regulator"
        />

        <Input
          label="Tax Identification Number"
          type="text"
          placeholder="e.g. UTR 1234567890"
          value={data?.taxId || ""}
          onChange={handleChange("taxId")}
          description="Corporation tax reference or equivalent"
        />

        <div className="md:col-span-2">
          <label className="block text-sm font-caption font-medium text-foreground mb-1">
            Additional Jurisdictions (if applicable)
          </label>
          <textarea
            value={data?.additionalJurisdictions || ""}
            onChange={handleChange("additionalJurisdictions")}
            placeholder="List any additional jurisdictions where your entity is regulated or has reporting obligations..."
            rows={3}
            className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground font-caption text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          />
        </div>
      </div>
    </div>
  );
}